// Central module-permission helpers, shared by the usePermissions hook (React),
// the <Can> component, and the api.ts write-guard (non-React).
//
// A module grant is Read (canView only) or Write (canCreate/canUpdate/canDelete).
// SUPERADMIN and ADMIN always have full write everywhere.

type AnyUser = any;

// ── current-user mirror ──────────────────────────────────────────────
// The axios interceptor is not a React component, so it can't read AuthContext.
// AuthProvider calls setCurrentUser(user) whenever the user changes; the
// interceptor then reads it through the pure helpers below.
let currentUser: AnyUser | null = null;
export const setCurrentUser = (user: AnyUser | null) => { currentUser = user; };
export const getCurrentUser = (): AnyUser | null => currentUser;

export const isPrivileged = (user: AnyUser | null): boolean =>
  user?.role === 'SUPERADMIN' || user?.role === 'ADMIN';

const permRowsFor = (user: AnyUser | null): any[] =>
  user ? [...(user.userRole?.permissions ?? []), ...(user.permissions ?? [])] : [];

export const canView = (user: AnyUser | null, moduleName: string): boolean => {
  if (!user) return false;
  if (isPrivileged(user)) return true;
  return permRowsFor(user).some((p) => p?.module?.name === moduleName && p.canView);
};

export const canWrite = (user: AnyUser | null, moduleName: string): boolean => {
  if (!user) return false;
  if (isPrivileged(user)) return true;
  return permRowsFor(user).some(
    (p) => p?.module?.name === moduleName && (p.canCreate || p.canUpdate || p.canDelete)
  );
};

// Create-only check: true for privileged users or a grant with canCreate. Distinct from
// canWrite so a "create but not view" grant (e.g. taka data-entry) is expressible on its own.
export const canCreate = (user: AnyUser | null, moduleName: string): boolean => {
  if (!user) return false;
  if (isPrivileged(user)) return true;
  return permRowsFor(user).some((p) => p?.module?.name === moduleName && p.canCreate);
};

// Edit-only check (canUpdate). Granular counterpart to canWrite so an "edit but not delete"
// grant is expressible on its own — used to gate Edit controls independently of Delete.
export const canUpdate = (user: AnyUser | null, moduleName: string): boolean => {
  if (!user) return false;
  if (isPrivileged(user)) return true;
  return permRowsFor(user).some((p) => p?.module?.name === moduleName && p.canUpdate);
};

// Delete-only check (canDelete). Gates Delete controls independently of create/edit.
export const canDelete = (user: AnyUser | null, moduleName: string): boolean => {
  if (!user) return false;
  if (isPrivileged(user)) return true;
  return permRowsFor(user).some((p) => p?.module?.name === moduleName && p.canDelete);
};

// True if the user can write to at least one module. A user for whom this is false
// is "pure read-only" — the write-guard blocks every write they attempt (safety net).
export const hasAnyWriteAccess = (user: AnyUser | null): boolean => {
  if (!user) return false;
  if (isPrivileged(user)) return true;
  return permRowsFor(user).some((p) => p.canCreate || p.canUpdate || p.canDelete);
};

// ── write-guard: API path → module name ──────────────────────────────
// Maps the first segment of an API path (the route mount prefix in server.js)
// to the module `name` used by the permission rows. Only single-module,
// clearly-owned prefixes are listed; anything not here is left unguarded
// (fail open) so shared/read flows never break.
const API_PREFIX_TO_MODULE: Record<string, string> = {
  'yarn-masters': 'yarn-inward',
  'yarn-outwards': 'yarn-outward',
  'taka-inventory': 'taka-inventory',
  'taka-groups': 'taka-inventory',
  'beam-masters': 'beam-master',
  stock: 'stock',
  clients: 'clients',
  'client-addresses': 'clients',
  qualities: 'qualities',
  purchases: 'purchases',
  'purchase-categories': 'purchases',
  sales: 'sales',
  salary: 'salary',
  machines: 'machines',
  'machine-groups': 'machines',
  employees: 'employees',
  chips: 'chip-management',
  gps: 'gps-management',
  roles: 'role-management',
  fingerprint: 'fingerprint-management',
  'fingerprint-devices': 'fingerprint-management',
  challans: 'stock',
  bills: 'stock',
  beams: 'beams',
  'beam-logs': 'beams',
  production: 'production',
  'machine-logs': 'machines',
  'meter-entries': 'production',
  'rapier-machine-logs': 'machines',
  yarns: 'qualities',
};

// Prefixes whose writes are NEVER blocked: non-mutating (auth, ocr scan) or
// must work for everyone incl. no-module staff (attendance/shift clock-in/out).
const WRITE_GUARD_ALLOWLIST = new Set(['auth', 'ocr', 'attendance', 'shift']);

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);
export const isWriteMethod = (method?: string): boolean =>
  !!method && WRITE_METHODS.has(method.toLowerCase());

// First path segment of an API url, e.g. '/yarn-masters/12?x=1' -> 'yarn-masters'.
const firstPathSegment = (url: string): string =>
  url
    .replace(/^https?:\/\/[^/]+/, '') // strip origin if absolute
    .replace(/^\/?(api\/)?/, '')       // strip leading slash + optional api/
    .split(/[/?#]/)[0]
    .toLowerCase();

export const moduleForApiPath = (url: string): string | null => {
  const seg = firstPathSegment(url);
  if (!seg || WRITE_GUARD_ALLOWLIST.has(seg)) return null;
  return API_PREFIX_TO_MODULE[seg] ?? null;
};

// Returns a label to block this write request, or null if it should be allowed.
export const blockedModuleForRequest = (method?: string, url?: string): string | null => {
  if (!isWriteMethod(method) || !url) return null;
  if (isPrivileged(currentUser)) return null;                 // admins bypass
  const seg = firstPathSegment(url);
  if (!seg || WRITE_GUARD_ALLOWLIST.has(seg)) return null;    // never block non-mutating / allowlisted paths
  const moduleName = API_PREFIX_TO_MODULE[seg];
  if (moduleName) return canWrite(currentUser, moduleName) ? null : moduleName;
  // Unmapped write endpoint: block for pure read-only users (safety net so no endpoint
  // slips through); allow for users who can write somewhere (avoid false-blocking them).
  if (currentUser && !hasAnyWriteAccess(currentUser)) return 'read-only';
  return null;
};
